/**
 * 前端 Storage 操作工具函数（TS 版本）
 * 兼容原生字符串/JSON 字符串两种存储格式，自动处理序列化/反序列化
 * @param {string} type 存储类型：'local' (默认) / 'session'
 * @returns {Object} 包含 get/set/remove/clear 方法的操作对象
 */
const createStorage = (type: 'local' | 'session' = 'local') => {
  // 校验存储类型，绑定对应的 Storage 实例
  const storage: Storage = type === 'session' ? sessionStorage : localStorage;

  /**
   * 智能解析存储数据（核心优化：兼容字符串/JSON 字符串）
   * @param data 从 Storage 读取的原始字符串
   * @returns 解析后的对象/原始字符串
   */
  const smartParse = (data: string): any => {
    if (!data) return null;
    try {
      // 优先尝试解析为 JSON（对象/数组）
      return JSON.parse(data);
    } catch (error) {
      // 解析失败，说明是原生字符串，直接返回
      return data;
    }
  };

  return {
    /**
     * 存储数据（统一序列化为字符串，保证格式一致性）
     * @param key 存储键名（非空）
     * @param value 存储值（支持任意类型：字符串/对象/数组等）
     */
    set: <T>(key: string, value: T): void => {
      if (!key) throw new Error('存储键名 key 不能为空');
      try {
        // 统一序列化：无论原始值是字符串还是对象，都转为 JSON 字符串
        // 避免手动存储原生字符串导致格式混乱
        const data = JSON.stringify(value);
        storage.setItem(key, data);
      } catch (error) {
        console.error('Storage 存储失败：', error);
      }
    },

    /**
     * 读取数据（智能解析，兼容原生字符串/JSON 字符串）
     * @param key 读取键名（非空）
     * @param defaultValue 读取失败/无数据时的默认值（可选，默认 null）
     * @returns 解析后的原始数据（对象/数组/字符串）
     */
    get: <T>(key: string, defaultValue: T | null = null): T | null => {
      if (!key) throw new Error('读取键名 key 不能为空');
      try {
        const rawData = storage.getItem(key);
        if (!rawData) return defaultValue;

        // 智能解析：兼容两种格式
        const parsedData = smartParse(rawData);
        return (parsedData !== null ? parsedData : defaultValue) as T;
      } catch (error) {
        console.error('Storage 读取失败：', error);
        return defaultValue;
      }
    },

    /**
     * 删除指定键名的数据
     * @param key 要删除的键名（非空）
     */
    remove: (key: string): void => {
      if (!key) throw new Error('删除键名 key 不能为空');
      try {
        storage.removeItem(key);
      } catch (error) {
        console.error('Storage 删除失败：', error);
      }
    },

    /**
     * 清空当前类型的所有存储数据
     */
    clear: (): void => {
      try {
        storage.clear();
      } catch (error) {
        console.error('Storage 清空失败：', error);
      }
    }
  };
};

// 导出实例
export const localStore = createStorage('local');
export const sessionStore = createStorage('session');

// 导出类型定义
export type StorageInstance = ReturnType<typeof createStorage>;
